import { createSelector } from '@reduxjs/toolkit';
import { getCurrentAccountId } from 'redux/selectors/accounts';
import type { IState } from 'redux/state';
import { getShouldAuthenticateToTic } from '~redux/selectors/user';
import { ticApi } from './api-slice/tic.apiSlice';

const readAuthState = (state: IState) => state.ticAuth;
const readTicApiSlice = (state: IState) => state[ticApi.reducerPath];

export const getIsTicAuthenticated = createSelector([readAuthState], (authState): boolean => {
  return authState.isAuthenticated;
});

/**
 * Returns true when it's safe to use the TIC API.
 */
export const getUseTicAPI = createSelector(
  [getShouldAuthenticateToTic, getIsTicAuthenticated],
  (enabled, authenticated) => enabled && authenticated
);

/**
 * Ref `tic.createApi.base.ts`
 */
export const getTicSessionsRTKQueryBase = createSelector([readTicApiSlice], localState => {
  return ticApi.endpoints.getTicSession.select(undefined)({
    [ticApi.reducerPath]: localState,
  });
});

/**
 * Returns TIC user's `personalSpace`, and the `workspace` that maps to the current Timely account.
 * Most useful when interacting with the TIC API which often needs to know the TIC account ID.
 */
export const getContextualTicAccounts = createSelector(
  [getTicSessionsRTKQueryBase, getCurrentAccountId],
  ({ data }, currentTimelyAccountId) => {
    return {
      user: data?.tic_user,
      personalSpace: data?.tic_accounts?.find(ticAccount => ticAccount.type === 'personal'),
      workspace: data?.tic_accounts?.find(
        ticAccount => ticAccount.timely_account_id === currentTimelyAccountId
      ),
    };
  }
);

/**
 * Whether a TIC workspace maps to the current Timely account.
 *
 * Every task query in the linked-work pickers keys off `workspace?.id` and skips without it, so
 * anything that decides whether a linked-work surface should EXIST has to require the same thing —
 * otherwise a tenant with only a personal space renders pickers that can never load a row
 * (TT-5457). Not the negation of `getHasSettledWithoutTicWorkspace`: this one is false while the
 * session is still resolving, that one is false both then and when a workspace exists.
 */
export const getHasTicWorkspace = createSelector(
  [getContextualTicAccounts],
  ({ workspace }): boolean => workspace?.id != null
);

/**
 * True when this account has NO TIC workspace and none is still on its way.
 *
 * `workspace == null` alone cannot carry that: it is equally the answer before the session has
 * resolved and after it resolved without one. Anything that treats an absent workspace as a
 * transient "not yet" therefore waits forever on a tenant that has no workspace at all. Three
 * states, not two:
 *
 * - session never dispatched (`isUninitialized`) — `TicContextProvider` only exchanges when
 *   `enabled={shouldAuthenticateToTic}`, so with TIC inapplicable nothing will ever dispatch it and
 *   uninitialized is DURABLE. With TIC applicable the very same state is the mount frame, before
 *   the exchange has run, and must stay transient.
 * - session settled (`isSuccess` / `isError`) with no matching workspace — durable.
 * - session in flight — transient by definition.
 */
export const getHasSettledWithoutTicWorkspace = createSelector(
  [getContextualTicAccounts, getTicSessionsRTKQueryBase, getShouldAuthenticateToTic],
  ({ workspace }, session, shouldAuthenticateToTic): boolean => {
    if (workspace?.id != null) return false;
    if (session.isUninitialized) return !shouldAuthenticateToTic;
    return session.isSuccess || session.isError;
  }
);
